/**
 * Lazy-loading for variant complementary products
 * Fetches complementary products dynamically when variant changes
 * to avoid rendering all variants' products in the initial DOM
 */

(function() {
  // Cache for fetched complementary products to avoid duplicate requests
  const complementaryCache = new Map();

  // Track pending requests to avoid duplicates
  const pendingRequests = new Map();

  // Debounce timers per element
  const debounceTimers = new Map();

  // Track the expected variant ID per element to ignore stale responses
  const expectedVariantId = new Map();

  // Keep variant changes feeling instant. Requests are still guarded by the
  // pending request map, so rapid clicks do not create duplicate network work.
  const DEBOUNCE_DELAY = 0;

  function getProductIdsFromList(listContainer) {
    if (!listContainer) return [];

    return Array.from(listContainer.querySelectorAll('.featured-product-item[data-product-id]'))
      .map(item => String(item.getAttribute('data-product-id')))
      .filter(Boolean);
  }

  function getProductIdFromComplementaryEntry(entry) {
    if (!entry) return null;

    if (typeof entry === 'number' || typeof entry === 'string') {
      return String(entry);
    }

    if (entry.id) {
      return String(entry.id);
    }

    if (entry.product && entry.product.id) {
      return String(entry.product.id);
    }

    return null;
  }

  function getConfiguredProductIdsForVariant(variantId) {
    const dataElement = document.querySelector('[data-complementary-products]');
    if (!dataElement) return null;

    try {
      const data = JSON.parse(dataElement.textContent || '{}');
      const entries = data.variants_complementary && data.variants_complementary[String(variantId)];

      if (!Array.isArray(entries)) {
        return null;
      }

      return entries
        .map(getProductIdFromComplementaryEntry)
        .filter(Boolean);
    } catch (error) {
      return null;
    }
  }

  function productIdListsMatch(firstList, secondList) {
    if (!Array.isArray(firstList) || !Array.isArray(secondList)) return false;
    if (firstList.length !== secondList.length) return false;

    return firstList.every((id, index) => id === secondList[index]);
  }

  function refreshFlickityLayout(element) {
    if (!element.flickityInstance) return;

    try {
      element.flickityInstance.reloadCells();
      element.flickityInstance.resize();
    } catch (error) {
      // Flickity can be between states while variants are changing.
    }
  }

  function lockComplementaryHeight(element) {
    const listContainer = element.querySelector('.complementary-product-list');
    const viewport = element.querySelector('.flickity-viewport');

    if (!listContainer) return;

    const currentHeight = Math.max(
      viewport ? viewport.offsetHeight : 0,
      listContainer.offsetHeight
    );

    if (currentHeight <= 0) return;

    const lockedHeight = `${currentHeight}px`;
    listContainer.style.setProperty('--complementary-loading-min-height', lockedHeight);
    listContainer.style.setProperty('--complementary-loading-height', lockedHeight);
    listContainer.style.height = lockedHeight;
    listContainer.style.minHeight = lockedHeight;
    listContainer.style.overflow = 'hidden';

    if (viewport) {
      viewport.style.height = lockedHeight;
      viewport.style.minHeight = lockedHeight;
    }
  }

  function unlockComplementaryHeight(element) {
    const listContainer = element.querySelector('.complementary-product-list');
    const viewport = element.querySelector('.flickity-viewport');

    if (listContainer) {
      listContainer.style.removeProperty('--complementary-loading-min-height');
      listContainer.style.removeProperty('--complementary-loading-height');
      listContainer.style.height = '';
      listContainer.style.minHeight = '';
      listContainer.style.overflow = '';
    }

    if (viewport) {
      viewport.style.minHeight = '';
    }
  }

  function syncFlickityViewportHeight(element) {
    const viewport = element.querySelector('.flickity-viewport');
    const cells = Array.from(element.querySelectorAll('.featured-product-item'))
      .filter(item => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

    if (!viewport || cells.length === 0) return 0;

    const tallestCell = Math.max(...cells.map(item => item.offsetHeight || 0));
    if (tallestCell > 0) {
      viewport.style.height = `${tallestCell}px`;
    }

    return tallestCell;
  }

  function finishComplementaryLayout(element) {
    refreshFlickityLayout(element);
    syncFlickityViewportHeight(element);

    requestAnimationFrame(() => {
      refreshFlickityLayout(element);
      syncFlickityViewportHeight(element);

      requestAnimationFrame(() => {
        refreshFlickityLayout(element);
        syncFlickityViewportHeight(element);
        const listContainer = element.querySelector('.complementary-product-list');
        if (listContainer) {
          listContainer.classList.remove('is-loading');
        }
        unlockComplementaryHeight(element);
      });
    });
  }

  function syncExistingItemsToVariant(element, variantId) {
    const listContainer = element.querySelector('.complementary-product-list');
    if (!listContainer) return;

    listContainer.setAttribute('data-variant-id', variantId);
    listContainer.classList.remove('is-loading');
    element.style.display = '';

    listContainer.querySelectorAll('.featured-product-item').forEach(item => {
      item.setAttribute('data-variant-id', variantId);
      item.variantId = String(variantId);
      item.style.display = '';

      const checkbox = item.querySelector('.featured-product__checkbox[data-variant-id]');
      if (checkbox) {
        checkbox.setAttribute('data-variant-id', variantId);
      }
    });

    handleVisibility(element);
    refreshFlickityLayout(element);
    unlockComplementaryHeight(element);
  }

  /**
   * Fetches complementary products HTML for a specific variant
   * Uses Shopify's Section Rendering API
   */
  async function fetchComplementaryProducts(productHandle, variantId, sectionId) {
    const cacheKey = `${productHandle}-${variantId}`;

    // Return cached result if available
    if (complementaryCache.has(cacheKey)) {
      return complementaryCache.get(cacheKey);
    }

    // If there's already a pending request for this key, return that promise
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }

    const fetchPromise = (async () => {
      try {
        const url = `${window.routes.rootUrlWithoutSlash}/products/${productHandle}?variant=${variantId}&section_id=${sectionId}`;
        const response = await fetch(url);

        if (!response.ok) {
          // If rate limited, wait and retry once
          if (response.status === 429) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const retryResponse = await fetch(url);
            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            const html = await retryResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const complementaryList = doc.querySelector('.complementary-product-list');
            const result = complementaryList ? complementaryList.innerHTML : '';
            complementaryCache.set(cacheKey, result);
            return result;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract just the complementary products list content
        const complementaryList = doc.querySelector('.complementary-product-list');
        const result = complementaryList ? complementaryList.innerHTML : '';

        // Cache the result
        complementaryCache.set(cacheKey, result);

        return result;
      } catch (error) {
        console.error('Error fetching complementary products:', error);
        return null;
      } finally {
        // Remove from pending requests
        pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    pendingRequests.set(cacheKey, fetchPromise);

    return fetchPromise;
  }

  /**
   * Destroys the existing Flickity instance if present
   */
  function destroyFlickity(element) {
    if (element.flickityInstance) {
      try {
        element.flickityInstance.destroy();
      } catch (e) {
        // Flickity may already be destroyed
      }
      element.flickityInstance = null;
    }
  }

  /**
   * Recreates the Flickity slideshow after content update
   */
  function recreateFlickity(element, variantId) {
    const listContainer = element.querySelector('.complementary-product-list');
    if (!listContainer) return;

    // Check if Flickity is available
    if (typeof Flickity === 'undefined') {
      console.warn('Flickity not available for complementary products carousel');
      listContainer.classList.remove('is-loading');
      unlockComplementaryHeight(element);
      return;
    }

    // Use a longer delay and double-check the variant ID before initializing
    setTimeout(() => {
      // Verify this is still the expected variant before creating Flickity
      const elementId = element.getAttribute('section-id') || 'default';
      const currentExpected = expectedVariantId.get(elementId);

      if (currentExpected !== String(variantId)) {
        // A newer variant was selected, don't initialize Flickity for this stale request
        return;
      }

      // Destroy any existing instance first (safety check)
      destroyFlickity(element);

      try {
        // Use FlickityFade if available (theme uses it), otherwise regular Flickity
        const FlickityClass = window.FlickityFade || Flickity;

        element.flickityInstance = new FlickityClass(listContainer, {
          pageDots: false,
          watchCSS: true,
          groupCells: true,
          cellAlign: 'left',
          prevNextButtons: true,
          wrapAround: true,
          percentPosition: true,
          resize: false,
          draggable: false
        });
        finishComplementaryLayout(element);
      } catch (e) {
        console.error('Error creating Flickity instance:', e);
        listContainer.classList.remove('is-loading');
        unlockComplementaryHeight(element);
      }
    }, 50);
  }

  /**
   * Updates the complementary products display for a variant
   */
  async function updateComplementaryProducts(element, variantId) {
    const productHandle = element.getAttribute('product-handle');
    const sectionId = element.getAttribute('section-id');
    const listContainer = element.querySelector('.complementary-product-list');
    const elementId = sectionId || 'default';

    if (!productHandle || !sectionId || !listContainer) {
      return;
    }

    lockComplementaryHeight(element);

    // Store the expected variant ID for this element
    expectedVariantId.set(elementId, String(variantId));

    // Check if we're already showing this variant's products
    const currentVariantId = listContainer.getAttribute('data-variant-id');
    if (currentVariantId === String(variantId)) {
      syncExistingItemsToVariant(element, variantId);
      return;
    }

    const currentProductIds = getProductIdsFromList(listContainer);
    const nextProductIds = getConfiguredProductIdsForVariant(variantId);
    if (nextProductIds && productIdListsMatch(currentProductIds, nextProductIds)) {
      syncExistingItemsToVariant(element, variantId);
      return;
    }

    // Keep the current carousel in place while the replacement HTML is fetched.
    listContainer.classList.add('is-loading');

    // Fetch new complementary products
    const html = await fetchComplementaryProducts(productHandle, variantId, sectionId);

    // Check if this is still the expected variant (user may have clicked another swatch)
    const currentExpected = expectedVariantId.get(elementId);
    if (currentExpected !== String(variantId)) {
      // This response is stale, ignore it
      return;
    }

    if (html === null) {
      listContainer.classList.remove('is-loading');
      unlockComplementaryHeight(element);
      return;
    }

    destroyFlickity(element);
    listContainer.setAttribute('data-variant-id', variantId);
    listContainer.innerHTML = html;

    // Handle visibility
    handleVisibility(element);

    // Recreate Flickity carousel with new content
    const items = listContainer.querySelectorAll('.featured-product-item');
    if (items.length > 0) {
      recreateFlickity(element, variantId);
    } else {
      unlockComplementaryHeight(element);
    }

    // Dispatch event for other components
    element.dispatchEvent(new CustomEvent('complementary-products:updated', {
      detail: { variantId },
      bubbles: true
    }));
  }

  /**
   * Handles visibility of the complementary products section
   */
  function handleVisibility(element) {
    const listContainer = element.querySelector('.complementary-product-list');
    const items = listContainer ? listContainer.querySelectorAll('.featured-product-item') : [];

    // Hide section if no complementary products
    if (items.length === 0) {
      element.style.display = 'none';
    } else {
      element.style.display = '';
    }
  }

  /**
   * Debounced update function to prevent rapid-fire requests
   */
  function debouncedUpdate(element, variantId) {
    const elementId = element.getAttribute('section-id') || 'default';

    // Clear any existing timer for this element
    if (debounceTimers.has(elementId)) {
      clearTimeout(debounceTimers.get(elementId));
    }

    // Update expected variant ID immediately
    expectedVariantId.set(elementId, String(variantId));

    const listContainer = element.querySelector('.complementary-product-list');
    const currentProductIds = getProductIdsFromList(listContainer);
    const nextProductIds = getConfiguredProductIdsForVariant(variantId);
    const needsContentUpdate = !nextProductIds || !productIdListsMatch(currentProductIds, nextProductIds);

    if (!needsContentUpdate) {
      syncExistingItemsToVariant(element, variantId);
      return;
    }

    if (listContainer && needsContentUpdate) {
      lockComplementaryHeight(element);
      listContainer.classList.add('is-loading');
    }

    const runUpdate = () => {
      debounceTimers.delete(elementId);
      updateComplementaryProducts(element, variantId);
    };

    // Set new debounced timer
    const timer = setTimeout(runUpdate, DEBOUNCE_DELAY);

    debounceTimers.set(elementId, timer);
  }

  document.addEventListener('variant:complementary:updated', function(event) {
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;

    document.querySelectorAll('variant-complementary-products').forEach(element => {
      const listContainer = element.querySelector('.complementary-product-list');
      const currentProductIds = getProductIdsFromList(listContainer);
      const nextProductIds = getConfiguredProductIdsForVariant(variantId);

      if (nextProductIds && productIdListsMatch(currentProductIds, nextProductIds)) {
        syncExistingItemsToVariant(element, variantId);
      }
    });
  }, true);

  // Listen for variant changes from the product form
  document.addEventListener('variant:changed', function(event) {
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;

    // Find all complementary product elements and update them with debouncing
    document.querySelectorAll('variant-complementary-products').forEach(element => {
      debouncedUpdate(element, variantId);
    });
  });

  // Expose function globally for manual updates if needed
  window.updateComplementaryProducts = updateComplementaryProducts;
})();
